import { ServiceCategory } from './BookingFlow';

interface CategorySelectionProps {
  selectedCategory: ServiceCategory | null;
  onCategoryChange: (category: ServiceCategory) => void;
}

const categories = [
  {
    id: 'veterinary' as ServiceCategory,
    name: 'Veterinary & Medical Care',
    description: 'Medical checkups, vaccinations, treatments',
    icon: '🏥',
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'grooming' as ServiceCategory,
    name: 'Grooming & Hygiene',
    description: 'Bathing, trimming, nail care, styling',
    icon: '✂️',
    color: 'from-purple-500 to-indigo-500'
  },
  {
    id: 'boarding' as ServiceCategory,
    name: 'Boarding, Daycare & Accommodation',
    description: 'Overnight stays, daycare, pet sitting',
    icon: '🏠',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'training' as ServiceCategory,
    name: 'Training & Behaviour',
    description: 'Obedience, behavior, skills training',
    icon: '🎓',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'walking' as ServiceCategory,
    name: 'Walking & Sitting Services',
    description: 'Daily walks, exercise, pet sitting',
    icon: '🚶',
    color: 'from-green-500 to-teal-500'
  },
  {
    id: 'breeding' as ServiceCategory,
    name: 'Breeding & Genetics',
    description: 'Breeding services, genetic counseling',
    icon: '🧬',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'retail' as ServiceCategory,
    name: 'Pet Retail & Supplies',
    description: 'Pet food, toys, accessories, supplies',
    icon: '🛒',
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'nutrition' as ServiceCategory,
    name: 'Nutrition & Wellness',
    description: 'Diet planning, supplements, wellness',
    icon: '🥗',
    color: 'from-lime-500 to-green-500'
  },
  {
    id: 'transportation' as ServiceCategory,
    name: 'Transportation & Travel',
    description: 'Pet transport, travel arrangements',
    icon: '🚗',
    color: 'from-sky-500 to-blue-500'
  },
  {
    id: 'insurance' as ServiceCategory,
    name: 'Insurance & Legal Services',
    description: 'Pet insurance, legal documentation',
    icon: '📋',
    color: 'from-slate-500 to-gray-500'
  },
  {
    id: 'identification' as ServiceCategory,
    name: 'Identification, Registration & Tech',
    description: 'Microchipping, registration, pet tech',
    icon: '💻',
    color: 'from-cyan-500 to-teal-500'
  },
  {
    id: 'endoflife' as ServiceCategory,
    name: 'End-of-Life & Memorial Services',
    description: 'Euthanasia, cremation, memorial services',
    icon: '🕯️',
    color: 'from-gray-600 to-slate-600'
  },
  {
    id: 'rescue' as ServiceCategory,
    name: 'Rescue, Welfare & Community',
    description: 'Animal rescue, shelters, community',
    icon: '❤️',
    color: 'from-pink-500 to-rose-500'
  },
  {
    id: 'events' as ServiceCategory,
    name: 'Events, Media & Lifestyle',
    description: 'Photography, events, pet entertainment',
    icon: '📸',
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'education' as ServiceCategory,
    name: 'Education & Research',
    description: 'Pet care education, research services',
    icon: '📚',
    color: 'from-amber-500 to-yellow-500'
  }
];

export function CategorySelection({ selectedCategory, onCategoryChange }: CategorySelectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2 dark:text-white">Choose Professional Category</h3>
        <p className="text-gray-600 dark:text-white/70">Select the type of service you need</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => onCategoryChange(category.id)}
            className={`text-left p-6 rounded-xl border-2 transition-all ${
              selectedCategory === category.id
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500 shadow-lg'
                : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30 hover:shadow-md'
            }`}
          >
            <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center text-3xl mb-4`}>
              {category.icon}
            </div>
            <h4 className="mb-2 dark:text-white">{category.name}</h4>
            <p className="text-sm text-gray-600 dark:text-white/60">{category.description}</p>
          </button>
        ))}
      </div>

      {selectedCategory && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-4">
          <p className="text-sm text-green-800 dark:text-green-300">
            ✓ <strong>{categories.find(c => c.id === selectedCategory)?.name}</strong> selected. 
            The booking flow will adapt to this service type.
          </p>
        </div>
      )}
    </div>
  );
}